import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AuthenticatedUser, SchoolWorkspace } from '../models/authenticated.user';
import { environment } from '../../../../environments/environment';
import { LonniyireLocalstorageService, StorageKey } from '../../../shared/services/lonniyire-localstorage-service';

export type LoginRequest = {
  identifier: string;
  password: string;
};

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private http = inject(HttpClient);
  private readonly localStorageService = inject(LonniyireLocalstorageService);

  private apiUrl = `${environment.apiUrl}/v1/auth`;
  private readonly userSignal = signal<AuthenticatedUser | undefined>(undefined);
  readonly user: Signal<AuthenticatedUser | undefined> = this.userSignal.asReadonly();

  private readonly isAuthenticatedSignal = signal<boolean>(false);
  private readonly activeSchoolIdSignal = signal<string | null>(null);
  private readonly activeSchoolSignal = signal<SchoolWorkspace | null>(null);
  readonly isAuthenticated: Signal<boolean> = this.isAuthenticatedSignal.asReadonly();
  activeSchoolId = this.activeSchoolIdSignal.asReadonly();
  activeSchool = this.activeSchoolSignal.asReadonly();

  readonly authState = computed(() => ({
    user: this.userSignal(),
    isAuthenticated: this.isAuthenticatedSignal(),
  }));

  /**
   * Check if user is currently authenticated
   */
  private checkAuthStatus(): boolean {
    return !!this.userSignal();
  }

  /**
   * Login user with username, email, or phone and password
   * Tokens are stored in httpOnly cookies by the backend
   * User data is stored in memory signal only
   */
  login(identifier: string, password: string): Observable<AuthenticatedUser> {
    const loginRequest: LoginRequest = { identifier, password };

    return this.http.post<AuthenticatedUser>(`${this.apiUrl}/login`, loginRequest).pipe(
      tap((user) => {
        this.userSignal.set(user);
        this.restoreActiveSchool(user);

        this.isAuthenticatedSignal.set(true);
      }),
    );
  }

  /**
   * Logout user
   * HttpOnly cookies are automatically cleared by the backend
   */
  logout(): Observable<unknown> {
    return this.http.post(`${this.apiUrl}/logout`, {}).pipe(
      tap(() => {
        this.clearAuthState();
      }),
    );
  }

  /**
   * Reset user password
   * @param passwordResetRequest Object containing old and new passwords
   */
  resetPassword(passwordResetRequest: { oldPassword: string; newPassword: string }): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/reset-password`, passwordResetRequest);
  }

  /**
   * Clear local authentication state
   */
  clearAuthState(): void {
    this.userSignal.set(undefined);
    this.isAuthenticatedSignal.set(false);
    this.activeSchoolIdSignal.set(null);
  }

  setActiveSchool(publicSchoolId: string): void {
    this.activeSchoolIdSignal.set(publicSchoolId);
    this.activeSchoolSignal.set(null);
    this.localStorageService.setItem(StorageKey.ACTIVE_SCHOOL_ID, publicSchoolId);
  }

  setActiveSchoolDetails(school: SchoolWorkspace): void {
    this.setActiveSchool(school.publicId);
    this.activeSchoolSignal.set(school);
  }

  loadSchool(publicSchoolId: string): Observable<SchoolWorkspace> {
    return this.http.get<SchoolWorkspace>(`${environment.apiUrl}/v1/schools/${publicSchoolId}/workspace`);
  }

  /**
   * Check if user is authenticated (synchronous)
   */
  isAuthenticatedSync(): boolean {
    return this.checkAuthStatus();
  }

  /**
   * Get current user as signal
   */
  getUser(): Signal<AuthenticatedUser | undefined> {
    return this.user;
  }

  /**
   * Verify authentication status with backend
   * Requests user info from /me endpoint
   * If user is logged in, returns the User object and updates signals
   */
  verifyAuth(): Observable<AuthenticatedUser> {
    return this.http.get<AuthenticatedUser>(`${this.apiUrl}/me`).pipe(
      tap((user) => {
        this.userSignal.set(user);
        this.restoreActiveSchool(user);
        this.isAuthenticatedSignal.set(true);
      }),
    );
  }

  private restoreActiveSchool(user: AuthenticatedUser): void {
    const schoolIds = Object.keys(user.permissions);
    const storedSchoolId = this.localStorageService.getItem(StorageKey.ACTIVE_SCHOOL_ID) as string | undefined;
    const activeSchoolId =
      storedSchoolId && user.permissions[storedSchoolId]
        ? storedSchoolId
        : schoolIds.length === 1
          ? schoolIds[0]
          : null;

    this.activeSchoolIdSignal.set(activeSchoolId);
    if (activeSchoolId) {
      this.localStorageService.setItem(StorageKey.ACTIVE_SCHOOL_ID, activeSchoolId);
      void this.loadSchool(activeSchoolId).subscribe({
        next: (school) => this.activeSchoolSignal.set(school),
      });
    }
  }
}
